// The Components item record — /components/items/:id (H100).
//
// Aaron 2026-09-28: "https://peak.aeronet.dev/app/components/flying - needs action
// menu and edit on rows and edit mode needs all tabs from legacy".
// Legacy: mod_parts mode=edit_item (parts.inc:169-181, parts_edit.inc), tabs per
// componentRecord.ts. Rule 0: Blank's record shell (BlankEdit.tsx) — Header with
// SaveFab + Close, TabBar, Content; the FORM tab inside <Form className="mui-form">,
// the grid tabs bring their own panel (ContentGridBox) and sit outside it (BP-47).
//
// SHARED, not forked: File = src/inventory/items/tabs/ItemFiles.tsx (the Inventory
// record's Files tab — Vault over resource_files, source "items").
// The ONE component editor (2026-10-07): the Components lists AND the aircraft record's
// Components tab open it ("editing a component from the aircraft should be the same";
// Aircraft and Components are dependent modules). `?from=` names the list to return to, or
// `from=aircraft&aircraft=<id>` the aircraft (componentRecord.componentRecordOrigin).
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Tab } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useSnackbar } from "notistack";

import { axios } from "../../api";
import { SaveFab } from "../../components/Buttons";
import Content from "../../components/Content";
import Header from "../../components/Header";
import { LinkRouter } from "../../components/Links";
import { LoadingView } from "../../components/Loading";
import { TabBar, TabPanel } from "../../components/Tabs";
import ItemFiles from "../../inventory/items/tabs/ItemFiles";
import type { ItemList } from "../../inventory/types";
import { useHasPermission } from "../../hooks/useHasPermission";
import type { AtaChapterRef } from "../../ata-chapters/types";
import { handleAPIErrors } from "../../utils/FormikErrors";
import { breadcrumbMenu } from "../ComponentLayout";
import {
  componentRecordCloseUrl,
  componentRecordOrigin,
  componentRecordTitle,
  ComponentTabKey,
  serialChangeNeedsReason,
  visibleComponentTabs,
} from "./componentRecord";
import ComponentAircraftDetails from "./tabs/ComponentAircraftDetails";
import ComponentGeneral, { ComponentGeneralValues, SNO_REASON_LABEL } from "./tabs/ComponentGeneral";
import ComponentHistory from "./tabs/ComponentHistory";
import ComponentSubComponents from "./tabs/ComponentSubComponents";
import { ConvertToFlyingDialog, useConvertToFlyingCheck } from "./ConvertToFlying";

type ComponentItem = ItemList & { aircraft_obj?: { registration?: string | null } | null };

interface PartInfo {
  notes?: string | null;
  part_superseded_by?: string | null;
  /** The part's ATA chapter -- the component's (Aaron 2026-10-09). */
  ata_chapter?: AtaChapterRef | null;
}

interface Props {
  config: { [key: string]: any };
  /** parts Edit grant (Component.tsx hasPartsEditPermission). */
  canSaveItem: boolean;
}

/** The wire's "YYYY-MM-DD" day, "" for none or the MySQL zero-date (TenantDatePicker's form). */
const isoDay = (raw?: string | null) => {
  const day = String(raw ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !day.startsWith("0000") ? day : "";
};

interface StatusOptions {
  status: number;
  allowed: number[];
  reason: string | null;
}

/** The record's Status choices (GET /v6/items/{id}/status-options, app/items/status_change.py):
 * legacy's General-tab select, offered only where legacy offers it. */
function useStatusOptions(itemId: number | undefined, reload: number): StatusOptions | null {
  const [options, setOptions] = useState<StatusOptions | null>(null);
  useEffect(() => {
    if (!itemId) return;
    let current = true;
    axios
      .get(`/v6/items/${itemId}/status-options`, { cache: false })
      // anything but the route's shape (an older API, a 404 page) offers no change
      .then((resp) => current && setOptions(Array.isArray(resp.data?.allowed) ? (resp.data as StatusOptions) : null))
      .catch(() => current && setOptions(null));
    return () => {
      current = false;
    };
  }, [itemId, reload]);
  return options;
}

const ComponentItemDetail: React.FC<Props> = ({ config, canSaveItem }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const formRef = useRef<FormikProps<ComponentGeneralValues>>(null);
  const closeAfterSave = useRef(false);
  const [item, setItem] = useState<ComponentItem | null>(null);
  const [part, setPart] = useState<PartInfo>({});
  const [tabKey, setTabKey] = useState<ComponentTabKey>("general");
  const [reload, setReload] = useState(0);
  const [converting, setConverting] = useState(false);
  // Convert to Flying (a Rotable back to Removed): legacy's parts_set_to_flying, the API's guards
  const canSetToFlying = useHasPermission("parts", "set_to_flying");
  const flyingCheck = useConvertToFlyingCheck(item?.id, reload);
  const [statusReload, setStatusReload] = useState(0);
  const statusOptions = useStatusOptions(item?.id, reload + statusReload);

  const origin = useMemo(() => componentRecordOrigin(searchParams), [searchParams]);
  const closeUrl = componentRecordCloseUrl(origin);
  // The Sub Components tab opens a child in this same record as `?from=${from}`, so the
  // child keeps where Close goes — an aircraft origin included.
  const from = origin.kind === "list" ? origin.mode : `aircraft&aircraft=${origin.aircraftId}`;

  useEffect(() => {
    if (!id) return;
    let current = true;
    axios
      .get(`/v6/items/${id}`, { cache: false })
      .then((resp) => current && setItem(resp.data as ComponentItem))
      .catch(() => {
        enqueueSnackbar(`Error fetching component ${id}`, { variant: "error" });
        navigate(closeUrl);
      });
    return () => {
      current = false;
    };
  }, [closeUrl, enqueueSnackbar, id, navigate, reload]);

  // Alt # and Notes are PART fields (parts.alt / parts.description); the item read
  // does not carry them.
  const partId = item?.part?.id;
  useEffect(() => {
    if (!partId) return;
    let current = true;
    axios
      .get(`/v6/parts/${partId}`, { cache: false })
      .then((resp) => current && setPart(resp.data ?? {}))
      .catch(() => current && setPart({}));
    return () => {
      current = false;
    };
  }, [partId]);

  // Legacy opens the record in preview without parts_Edit or when the item is
  // inactive (parts_tags.inc:423-440 edit-action-allowed).
  const locked = !canSaveItem || !item?.active;

  const tabs = useMemo(() => (item ? visibleComponentTabs(item, config) : []), [item, config]);
  const activeTab = tabs.some((t) => t.key === tabKey) ? tabKey : "general";

  const labelForStatus = (status: number | string | null | undefined) =>
    (config?.status_list && config.status_list[Number(status)]) ?? String(status ?? "");
  const statusChoices = useMemo(() => {
    if (!item || !statusOptions || statusOptions.allowed.length === 0) return [];
    return [Number(item.status), ...statusOptions.allowed].map((s) => ({ value: String(s), label: labelForStatus(s) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, item, statusOptions]);

  const initialValues: ComponentGeneralValues | null = useMemo(() => {
    if (!item) return null;
    const statusLabel =
      (config?.status_list && config.status_list[Number(item.status)]) ?? String(item.status ?? "");
    return {
      part_number: item.part?.number || "",
      part_superseded_by: part.part_superseded_by || "",
      part_name: item.part?.description || "",
      part_notes: part.notes || "",
      serial_number: item.serial_number || "",
      batch_number: item.batch?.number ?? item.release_number ?? "",
      quantity: item.quantity != null ? String(item.quantity) : "",
      unit: item.uom || "",
      status_label: statusLabel,
      status: String(item.status ?? ""),
      location: item.location || "",
      store: item.store || "",
      shelf: item.shelf || "",
      condition: item.condition || "",
      shelf_expiry: isoDay(item.shelf_expiry),
      inspection_date: isoDay(item.inspection_date),
      notes: item.notes || "",
      item_description: item.item_description || "",
      comment: item.comment || "",
      sno_reason: "",
      ata_chapter: part.ata_chapter ?? null,
    };
  }, [config, item, part]);

  if (!item || !initialValues) return <LoadingView />;

  const save = (values: ComponentGeneralValues, helpers: any) => {
    const originalSerial = item.serial_number || "";
    const serialChanged = values.serial_number !== originalSerial;
    // Legacy submitform() (parts_edit.js:88-106): no save without the reason.
    if (
      serialChangeNeedsReason(originalSerial, values.serial_number, config?.na_sn_list) &&
      !values.sno_reason.trim()
    ) {
      helpers.setSubmitting(false);
      closeAfterSave.current = false;
      helpers.setFieldError("sno_reason", SNO_REASON_LABEL);
      enqueueSnackbar(SNO_REASON_LABEL, { variant: "warning" });
      return;
    }
    const payload = {
      // Sent only when changed, and always with the reason: the API writes the
      // SNoChange parts_history row when the change needs one.
      ...(serialChanged ? { serial_number: values.serial_number, sno_reason: values.sno_reason } : {}),
      store: values.store,
      shelf: values.shelf,
      condition: values.condition,
      shelf_expiry: values.shelf_expiry || null,
      inspection_date: values.inspection_date || null,
      notes: values.notes,
      description: values.item_description,
      comment: values.comment,
      // items.unit travels as `uom` (ItemCreateUpdateBase alias); sent only when changed
      ...(values.unit !== (item.uom || "") ? { uom: values.unit } : {}),
    };
    // The ATA chapter is the PART's (parts.ata_chapter_id): every item of the P/N shares it,
    // so it is written to the part, and only when the user changed it.
    const chapterId = values.ata_chapter?.id ?? null;
    const chapterChanged = !!partId && chapterId !== (part.ata_chapter?.id ?? null);
    // Status first, through its own guarded route (legacy's update_item status block,
    // parts_tab_general_item_update.inc:407-458): a refusal stops the save with legacy's words.
    const statusChanged = values.status !== String(item.status ?? "");
    const statusWrite = statusChanged
      ? axios.post(`/v6/items/${item.id}/status`, { status: Number(values.status) }, { cache: false })
      : Promise.resolve(null);
    statusWrite
      .then(() => axios.put(`/v6/items/${item.id}`, payload, { cache: false }))
      .then(async (resp) => {
        if (statusChanged) setStatusReload((k) => k + 1);
        setItem(resp.data as ComponentItem);
        if (chapterChanged) {
          try {
            const partResp = await axios.patch(`/v6/parts/${partId}`, { ata_chapter_id: chapterId }, { cache: false });
            setPart((p) => ({ ...p, ata_chapter: partResp.data?.ata_chapter ?? null }));
          } catch (error: any) {
            // the item saved; say plainly that the chapter did not (e.g. 409: not on this tenant yet)
            helpers.setSubmitting(false);
            closeAfterSave.current = false;
            const detail = error?.response?.data?.detail;
            enqueueSnackbar(
              `Component saved, but not its ATA chapter${typeof detail === "string" ? `: ${detail}` : ""}`,
              { variant: "warning" }
            );
            return;
          }
        }
        helpers.setSubmitting(false);
        enqueueSnackbar("Component saved", { variant: "success" });
        if (closeAfterSave.current) {
          closeAfterSave.current = false;
          navigate(closeUrl);
        }
      })
      .catch((error) => {
        helpers.setSubmitting(false);
        closeAfterSave.current = false;
        handleAPIErrors(error, helpers.setErrors);
        const detail = error?.response?.data?.detail;
        enqueueSnackbar(typeof detail === "string" ? detail : "Error saving component", { variant: "error" });
      });
  };

  const headerActions =
    !locked && activeTab === "general"
      ? [
          <SaveFab
            key="save"
            onClick2={() => formRef.current?.submitForm()}
            onClick={() => {
              closeAfterSave.current = true;
              formRef.current?.submitForm();
            }}
          />,
        ]
      : [];

  return (
    <>
      <Header
        closeUrl={closeUrl}
        actions={headerActions}
        recordActions={{
          id: "component-record-actions",
          sections: [
            {
              label: "Status",
              items: [
                {
                  name: "Convert to flying",
                  onClick: () => setConverting(true),
                  hidden: !(canSetToFlying && flyingCheck?.allowed),
                },
              ],
            },
          ],
        }}
      >
        {origin.kind === "aircraft" ? (
          <LinkRouter to={closeUrl}>
            Aircraft / {item.aircraft_obj?.registration || `#${origin.aircraftId}`} / Components /{" "}
          </LinkRouter>
        ) : (
          <LinkRouter to={closeUrl}>Components / {breadcrumbMenu[closeUrl] ?? "Parts"} / </LinkRouter>
        )}
        <span data-testid="component-record-title">{componentRecordTitle(item, config)}</span>
      </Header>
      <TabBar
        value={tabs.findIndex((t) => t.key === activeTab)}
        onChange={(_e: React.SyntheticEvent<{}>, idx: unknown) => setTabKey(tabs[Number(idx)].key)}
      >
        {tabs.map((t, i) => (
          <Tab key={t.key} disableRipple label={t.label} id={`component-tab-${i}`} aria-controls={`component-tabpanel-${i}`} />
        ))}
      </TabBar>
      <Content>
        {tabs.map((t, i) => (
          <TabPanel key={t.key} value={tabs.findIndex((x) => x.key === activeTab)} index={i} data-testid={`component-${t.key}`}>
            {t.key === "general" ? (
              <Formik enableReinitialize initialValues={initialValues} innerRef={formRef} onSubmit={save}>
                <Form className="mui-form">
                  <ComponentGeneral
                    config={config}
                    locked={locked}
                    originalSerial={item.serial_number || ""}
                    hasPart={!!partId}
                    statusChoices={statusChoices}
                  />
                </Form>
              </Formik>
            ) : null}
            {t.key === "sub" ? <ComponentSubComponents itemId={item.id} locked={locked} from={from} /> : null}
            {t.key === "aircraft" ? <ComponentAircraftDetails itemId={item.id} locked={locked} /> : null}
            {t.key === "history" ? <ComponentHistory itemId={item.id} locked={locked} /> : null}
            {t.key === "file" ? <ItemFiles itemId={item.id} /> : null}
          </TabPanel>
        ))}
      </Content>
      <ConvertToFlyingDialog
        open={converting}
        itemId={item.id}
        config={config}
        onClose={() => setConverting(false)}
        onConverted={() => {
          setConverting(false);
          setReload((n) => n + 1);
        }}
      />
    </>
  );
};

export default ComponentItemDetail;
